// Dashboard Page
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, presupuestosAPI } from '../api/api';
import { Users, FileText, UtensilsCrossed, TrendingUp, Plus, AlertTriangle, CheckCircle2, ExternalLink, ChefHat, ShoppingCart } from 'lucide-react';
import AlertasCambioPrecio from '../components/AlertasCambioPrecio';
import CancelacionesCard from '../components/CancelacionesCard';
import OrdenCocinaDescargaModal from '../components/OrdenCocinaDescargaModal';
import useFormatoNuevo from '../hooks/useFormatoNuevo';
import { diasHastaFecha, enProximosDias, parsearFechaServidor, pendientesEventoConfirmado } from '../utils/pantallasUnDia';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Registrar componentes de ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Plugin: dibuja una marca horizontal (tick) a la altura del objetivo de cada mes
// sobre la columna del año en curso. Permite ver, en los meses cerrados y en el
// mes en curso, si la barra real llegó al objetivo (barra por encima del tick) o
// se quedó corta.
const objetivoMarkerPlugin = {
  id: 'objetivoMarker',
  afterDatasetsDraw(chart, args, opts) {
    if (!opts || !opts.activo || !opts.objetivo) return;
    const dsIndex = chart.data.datasets.findIndex(d => d.label === String(opts.year));
    if (dsIndex < 0) return;
    const meta = chart.getDatasetMeta(dsIndex);
    if (!meta || meta.hidden) return;
    const yScale = chart.scales.y;
    const { ctx } = chart;
    ctx.save();
    ctx.strokeStyle = opts.color || '#F87171';
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    const hastaMes = Number.isInteger(opts.hastaMes) ? opts.hastaMes : 12;
    opts.objetivo.forEach((val, m) => {
      if (m >= hastaMes) return; // meses cerrados y el mes en curso (no los futuros)
      const bar = meta.data[m];
      if (val == null || val <= 0 || !bar) return;
      const w = (bar.width || 18) * 0.85;
      const y = yScale.getPixelForValue(val);
      ctx.beginPath();
      ctx.moveTo(bar.x - w / 2, y);
      ctx.lineTo(bar.x + w / 2, y);
      ctx.stroke();
    });
    ctx.restore();
  }
};

const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  // Formato nuevo (en pruebas): la tarjeta de cancelaciones solo se ve con él activo
  const [formatoNuevo] = useFormatoNuevo();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ventasData, setVentasData] = useState(null);
  // Por año: { total, count, acumuladoHoy } para ticket medio y acumulado a fecha de hoy
  const [resumenVentas, setResumenVentas] = useState({});
  const [añoActual, setAñoActual] = useState(new Date().getFullYear());
  // Por defecto: año actual y año anterior
  const [añosComparar, setAñosComparar] = useState(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 1, currentYear]; // Ej: [2025, 2026]
  });
  const [loadingVentas, setLoadingVentas] = useState(false);
  const [tipoGrafico, setTipoGrafico] = useState('bar'); // 'bar' o 'line'
  const [eventosConPendientes, setEventosConPendientes] = useState([]);
  const [diasPendientes, setDiasPendientes] = useState(7); // 7, 15 o 30 días
  const [showOrdenesCocina, setShowOrdenesCocina] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      // Esperar a que el usuario esté autenticado
      if (!user) {
        return;
      }
      
      try {
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('❌ Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user]); // Dependencia: user - se ejecuta cuando el user cambia

  useEffect(() => {
    if (user) {
      fetchVentasData();
    }
  }, [añosComparar, user]); // Agregar user como dependencia

  const fetchVentasData = async () => {
    setLoadingVentas(true);
    try {
      // Obtener TODOS los presupuestos confirmados (sin límite de paginación)
      const response = await presupuestosAPI.getAll({ limit: 10000, estado: 'confirmado' });
      const presupuestos = response.data?.presupuestos || response.data || [];
      
      
      // Ya están filtrados por estado confirmado en el API
      const confirmados = presupuestos;
      
      // Organizar datos por año y mes - INICIALIZAR TODOS LOS AÑOS CON DATOS
      const ventasPorAño = {};
      
      // Primero, procesar todos los presupuestos para ver qué años tienen datos
      const añosConDatos = new Set();
      confirmados.forEach(p => {
        if (p.fecha_evento) {
          const fecha = new Date(p.fecha_evento);
          const año = fecha.getFullYear();
          añosConDatos.add(año);
        }
        // Multievento (M-37): también los años de sus jornadas
        Object.keys(p.importes_por_mes || {}).forEach((clave) => {
          const año = parseInt(String(clave).slice(0, 4), 10);
          if (!Number.isNaN(año)) añosConDatos.add(año);
        });
      });
      
      
      // Inicializar arrays para todos los años encontrados
      añosConDatos.forEach(año => {
        ventasPorAño[año] = Array(12).fill(0);
      });
      
      // Resumen por año: total, nº de eventos (para ticket medio) y acumulado a
      // fecha de hoy (mismo mes/día del año correspondiente, para comparar YTD).
      const resumen = {};
      const hoy = new Date();
      const hoyMes = hoy.getMonth();
      const hoyDia = hoy.getDate();

      // Llenar los datos
      confirmados.forEach(p => {
        // Multievento (M-37): cada jornada en su mes con el reparto del
        // servidor (`importes_por_mes`, base sin IVA: suplementos del evento,
        // alquiler y bebidas del evento en el mes de la primera jornada). El
        // evento cuenta una vez (ticket medio) en el año de su primera jornada.
        const porMes = p.importes_por_mes && typeof p.importes_por_mes === 'object' ? p.importes_por_mes : null;
        if (porMes && Object.keys(porMes).length > 0) {
          const claves = Object.keys(porMes).sort();
          claves.forEach((clave, idx) => {
            const año = parseInt(clave.slice(0, 4), 10);
            const mes = parseInt(clave.slice(5, 7), 10) - 1;
            if (Number.isNaN(año) || Number.isNaN(mes) || mes < 0 || mes > 11) return;
            const importe = Number(porMes[clave]) || 0;
            if (ventasPorAño[año]) ventasPorAño[año][mes] += importe;
            if (!resumen[año]) resumen[año] = { total: 0, count: 0, acumuladoHoy: 0 };
            resumen[año].total += importe;
            if (idx === 0) resumen[año].count += 1;
            // Acumulado hasta hoy: meses anteriores enteros; el mes de hoy si
            // la primera jornada de ese mes ya ha llegado
            const primeraDelMes = (p.jornadas || [])
              .map(j => String(j?.fecha || '').slice(0, 10))
              .filter(f => f.slice(0, 7) === clave)
              .sort()[0];
            const diaMes = primeraDelMes ? parseInt(primeraDelMes.slice(8, 10), 10) : 1;
            if (mes < hoyMes || (mes === hoyMes && diaMes <= hoyDia)) {
              resumen[año].acumuladoHoy += importe;
            }
          });
          return;
        }
        if (p.fecha_evento) {
          const fecha = new Date(p.fecha_evento);
          const año = fecha.getFullYear();
          const mes = fecha.getMonth();

          // Venta sin IVA del servidor (U-37, `venta_sin_iva`, misma función que
          // Rentabilidad): `precio_total` ya incluye los suplementos en todo
          // guardado actual, no se suman otra vez. Sin el campo (servidor
          // anterior), `importe_base` o `precio_total` tal cual.
          const ventaServidor = Number(p.venta_sin_iva);
          const importe = Number.isFinite(ventaServidor) && p.venta_sin_iva !== null && p.venta_sin_iva !== undefined
            ? ventaServidor
            : (Number(p.importe_base) || Number(p.precio_total) || Number(p.subtotal) || 0);

          if (ventasPorAño[año]) {
            ventasPorAño[año][mes] += importe;
          }

          // Resumen anual
          if (!resumen[año]) resumen[año] = { total: 0, count: 0, acumuladoHoy: 0 };
          resumen[año].total += importe;
          resumen[año].count += 1;
          // Acumulado hasta hoy: eventos cuya fecha (mes/día) es <= hoy
          const dia = fecha.getDate();
          if (mes < hoyMes || (mes === hoyMes && dia <= hoyDia)) {
            resumen[año].acumuladoHoy += importe;
          }
        }
      });

      setVentasData(ventasPorAño);
      setResumenVentas(resumen);
    } catch (error) {
      console.error('❌ Error fetching ventas:', error);
    } finally {
      setLoadingVentas(false);
    }
  };

  // Filtrar eventos próximos según los días seleccionados (todos, no solo pendientes)
  useEffect(() => {
    const filtrarEventosProximos = () => {
      if (!stats?.presupuestos_confirmados) return;
      
      const confirmados = stats.presupuestos_confirmados;
      
      // Multievento (M-38): fecha, hora, personas, lugar, líneas y hora de
      // llegada de su próxima jornada (`proxima_jornada` del servidor); un
      // evento de un día, los suyos
      const conJornada = confirmados.map(p => {
        const pj = p.proxima_jornada;
        if (!pj) return p;
        return {
          ...p,
          fecha_evento: pj.fecha || p.fecha_evento,
          hora_inicio: pj.hora_inicio || '',
          hora_llegada_estimada: pj.hora_llegada_estimada || '',
          menus_personalizados: Array.isArray(pj.menus_personalizados) ? pj.menus_personalizados : p.menus_personalizados,
          lugar_evento: pj.lugar || p.lugar_evento,
          num_personas_jornada: pj.num_personas,
          etiqueta_jornada: pj.etiqueta,
        };
      });

      // U-101 (lote 7): "próximos N días" incluye el día N, con la fecha local
      const eventosProximos = conJornada.filter(p => enProximosDias(p.fecha_evento, diasPendientes))
        .map(p => {
          // U-70 (lote 7): pendientes con los criterios de Por hacer (modo de
          // bebidas validado, Quién va, orden de personal extra, material...),
          // no con campos que ya no se escriben
          const pendientes = pendientesEventoConfirmado(p);
          return { ...p, tienePendientes: pendientes.length > 0, pendientes };
        })
        .sort((a, b) => String(a.fecha_evento || '').localeCompare(String(b.fecha_evento || '')))
        .slice(0, 15); // Mostrar máximo 15
      
      setEventosConPendientes(eventosProximos);
    };
    
    filtrarEventosProximos();
  }, [stats?.presupuestos_confirmados, diasPendientes]);

  const toggleAñoComparacion = (año) => {
    if (añosComparar.includes(año)) {
      if (añosComparar.length > 1) {
        setAñosComparar(añosComparar.filter(a => a !== año));
      }
    } else {
      // Mantener los años siempre en orden ascendente para que las barras salgan
      // de más antiguo (izquierda) a más reciente (derecha).
      setAñosComparar([...añosComparar, año].sort((a, b) => a - b));
    }
  };

  const colores = ['#1A9169', '#FFA500', '#3B82F6', '#EF4444', '#8B5CF6'];
  // Color fijo por año (no por posición) para que cada año conserve siempre su color
  // aunque se añadan/quiten años. 5 colores → años consecutivos no colisionan (el
  // selector muestra como máximo 5 años).
  const colorDeAño = (año) => colores[((año % colores.length) + colores.length) % colores.length];

  // ----- Proyección / objetivo del año en curso -----
  // Método (estable, no baila): objetivo de cada mes = MEDIA HISTÓRICA de ese
  // mismo mes (media de todos los eneros, todos los febreros...) ajustada por un
  // único FACTOR de ritmo del año. El factor sólo se calcula con los meses ya
  // CERRADOS (nunca el mes en curso a medias) y sólo se aplica a partir de 3
  // meses cerrados; antes de eso el objetivo es la media histórica pura.
  const MESES_CERRADOS_MINIMOS = 3;
  // Años COVID/arranque que distorsionan la media: 2020 y 2021 fueron anómalamente
  // bajos y hunden la base histórica, disparando el factor de ritmo. Se ignoran enteros.
  const ANIO_HISTORICO_MINIMO = 2022;
  // Además, ene-abr 2022 fueron coletazos del COVID (normalidad desde mayo 2022).
  const MESES_EXCLUIDOS_HISTORICO = { 2022: [0, 1, 2, 3] };
  const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));

  const proyeccion = (() => {
    if (!ventasData) return { activo: false };
    const currentYear = añoActual;
    if (!añosComparar.includes(currentYear)) return { activo: false };

    const añosPasados = Object.keys(ventasData)
      .map(Number)
      .filter(a => a < currentYear && a >= ANIO_HISTORICO_MINIMO && (ventasData[a] || []).some(v => v > 0));
    if (añosPasados.length === 0) return { activo: false };

    // 1) Media histórica por mes (excluyendo los meses anómalos marcados).
    const mediaMes = Array(12).fill(0);
    for (let m = 0; m < 12; m++) {
      const valores = [];
      añosPasados.forEach(a => {
        if ((MESES_EXCLUIDOS_HISTORICO[a] || []).includes(m)) return;
        valores.push((ventasData[a] || [])[m] || 0);
      });
      mediaMes[m] = valores.length ? valores.reduce((s, v) => s + v, 0) / valores.length : 0;
    }

    // 2) Factor de ritmo del año, sólo con meses ya cerrados (el mes en curso no cuenta).
    const hoy = new Date();
    const mesActual = hoy.getMonth();
    const mesesCerrados = mesActual; // meses 0..mesActual-1 están completos
    const real = ventasData[currentYear] || Array(12).fill(0);

    let factor = 1;
    let factorAplicado = false;
    if (mesesCerrados >= MESES_CERRADOS_MINIMOS) {
      let realCerrado = 0;
      let baseCerrado = 0;
      for (let m = 0; m < mesesCerrados; m++) {
        realCerrado += real[m] || 0;
        baseCerrado += mediaMes[m];
      }
      if (baseCerrado > 0) {
        factor = clamp(realCerrado / baseCerrado, 0.5, 2.0); // tope defensivo
        factorAplicado = true;
      }
    }

    // 3) Objetivo por mes, hueco pendiente (mes en curso + futuros) y total estimado.
    const objetivo = Array(12).fill(0);
    const falta = Array(12).fill(null);
    let totalEstimado = 0;
    let facturadoCerrado = 0;
    for (let m = 0; m < 12; m++) {
      objetivo[m] = mediaMes[m] * factor;
      if (m < mesActual) {
        totalEstimado += real[m] || 0; // mes cerrado: cuenta lo real
        facturadoCerrado += real[m] || 0;
      } else {
        falta[m] = Math.max(0, objetivo[m] - (real[m] || 0));
        totalEstimado += Math.max(objetivo[m], real[m] || 0);
      }
    }
    const previsionResto = totalEstimado - facturadoCerrado;
    return { activo: true, objetivo, falta, totalEstimado, facturadoCerrado, previsionResto, mesActual, factor, factorAplicado, mesesCerrados };
  })();

  const chartData = ventasData ? {
    labels: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    datasets: (() => {
      const ds = [];
      añosComparar.forEach((año) => {
        const color = colorDeAño(año);
        const esActual = año === añoActual && proyeccion.activo;

        if (tipoGrafico === 'bar') {
          // Barra sólida = importe confirmado (real) de cada mes.
          ds.push({
            label: año.toString(),
            data: ventasData[año] || Array(12).fill(0),
            backgroundColor: `${color}CC`,
            borderColor: color,
            borderWidth: 1,
            stack: año.toString() // cada año = su propia pila (quedan lado a lado)
          });
          // Sobre el año en curso, tono claro apilado = lo que falta p/ el objetivo.
          if (esActual) {
            ds.push({
              label: `${año} · previsión`,
              data: proyeccion.falta,
              backgroundColor: `${color}33`,
              borderColor: `${color}66`,
              borderWidth: 1,
              stack: año.toString()
            });
          }
        } else {
          ds.push({
            label: año.toString(),
            data: ventasData[año] || Array(12).fill(0),
            borderColor: color,
            backgroundColor: `${color}33`,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            fill: true
          });
          // En modo líneas, el objetivo va como línea discontinua desde el mes en curso.
          if (esActual) {
            ds.push({
              label: `${año} · objetivo`,
              data: proyeccion.objetivo.map((v, m) => (m >= proyeccion.mesActual ? v : null)),
              borderColor: `${color}99`,
              backgroundColor: 'transparent',
              borderDash: [6, 4],
              tension: 0.4,
              borderWidth: 2,
              pointRadius: 0,
              fill: false
            });
          }
        }
      });
      return ds;
    })()
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const val = context.parsed.y || 0;
            const euros = '€' + val.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            const lbl = context.dataset.label || '';
            if (lbl.includes('previsión')) return `Falta p/ objetivo: ${euros}`;
            if (lbl.includes('objetivo')) return `Objetivo: ${euros}`;
            return `${lbl}: ${euros}`;
          }
        }
      },
      // Config para el plugin objetivoMarker (marca del objetivo por mes en barras).
      objetivoMarker: {
        activo: !!(proyeccion.activo && tipoGrafico === 'bar'),
        objetivo: proyeccion.objetivo,
        year: añoActual,
        // Meses cerrados y también el mes en curso: si ya se ha superado el
        // objetivo, la franja de previsión es cero y la marca es la única pista
        // de dónde estaba. En los futuros no hace falta: el tope de la franja ya
        // coincide con el objetivo.
        hastaMes: proyeccion.mesActual + 1,
        color: '#F87171'
      }
    },
    scales: {
      x: {
        stacked: tipoGrafico === 'bar'
      },
      y: {
        beginAtZero: true,
        stacked: tipoGrafico === 'bar',
        ticks: {
          callback: function(value) {
            return '€' + value.toLocaleString();
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="dashboard-title">
          Bienvenido, {user?.nombre}
        </h1>
        <p className="text-gray-600">Panel de control de Duetto Manager</p>
      </div>

      {/* Alertas de Cambio de Precio */}
      <div className="mb-6">
        <AlertasCambioPrecio />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          title="Clientes Totales"
          value={stats?.clientes?.total || 0}
          subtitle={`${stats?.clientes?.activos || 0} activos, ${stats?.clientes?.leads || 0} leads`}
          color="bg-duetto-primary"
        />
        <StatCard
          icon={FileText}
          title="Presupuestos"
          value={stats?.presupuestos?.total || 0}
          subtitle={`${stats?.presupuestos?.pendientes || 0} pendientes`}
          color="bg-duetto-secondary"
        />
        <StatCard
          icon={UtensilsCrossed}
          title="Menús Activos"
          value={stats?.catalogo?.menus || 0}
          subtitle={`${stats?.catalogo?.platos || 0} platos disponibles`}
          color="bg-yellow-500"
        />
        <StatCard
          icon={TrendingUp}
          title="Confirmados"
          value={stats?.presupuestos?.confirmados || 0}
          subtitle="Este mes"
          color="bg-green-500"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Acciones Rápidas</h2>
        <div className={`grid grid-cols-1 gap-4 ${user?.rol === 'direccion' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <button 
            onClick={() => navigate('/presupuestos?nuevo=1')}
            className="p-4 border border-gray-200 rounded-lg hover:border-duetto-primary hover:bg-duetto-primary hover:bg-opacity-5 transition-colors text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Nuevo presupuesto</p>
                <p className="text-sm text-gray-500 mt-1">Abre directamente el alta del evento</p>
              </div>
              <Plus className="w-5 h-5 text-duetto-primary opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
          <button 
            onClick={() => setShowOrdenesCocina(true)}
            className="p-4 border border-gray-200 rounded-lg hover:border-duetto-primary hover:bg-duetto-primary hover:bg-opacity-5 transition-colors text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Órdenes de cocina</p>
                <p className="text-sm text-gray-500 mt-1">Varias órdenes en un PDF, eliges los días</p>
              </div>
              <ChefHat className="w-5 h-5 text-duetto-primary opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
          <button 
            onClick={() => navigate('/compras/necesidades')}
            className="p-4 border border-gray-200 rounded-lg hover:border-duetto-primary hover:bg-duetto-primary hover:bg-opacity-5 transition-colors text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Compra de la semana</p>
                <p className="text-sm text-gray-500 mt-1">Lista de compra de los próximos eventos</p>
              </div>
              <ShoppingCart className="w-5 h-5 text-duetto-primary opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
          {user?.rol === 'direccion' && (
            <a 
              href="/web"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 border border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-colors text-left group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Web Pública</p>
                  <p className="text-sm text-gray-500 mt-1">Ver página de clientes</p>
                </div>
                <ExternalLink className="w-5 h-5 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          )}
        </div>
      </div>

      {/* Widget de Eventos con Pendientes */}
      <div className={`bg-white rounded-xl shadow-sm border ${eventosConPendientes.length > 0 ? 'border-red-200' : 'border-gray-200'} p-6 mb-8`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${eventosConPendientes.length > 0 ? 'text-red-500' : 'text-gray-400'}`} />
            <h2 className="text-xl font-bold text-gray-900">Próximos Eventos</h2>
            {eventosConPendientes.length > 0 && (
              <div className="flex gap-2">
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  {eventosConPendientes.filter(e => !e.tienePendientes).length} listos
                </span>
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                  {eventosConPendientes.filter(e => e.tienePendientes).length} pendientes
                </span>
              </div>
            )}
          </div>
          {/* Selector de días */}
          <select
            value={diasPendientes}
            onChange={(e) => setDiasPendientes(parseInt(e.target.value))}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-duetto-primary focus:border-duetto-primary"
            >
              <option value={7}>Próximos 7 días</option>
              <option value={15}>Próximos 15 días</option>
              <option value={30}>Próximos 30 días</option>
            </select>
          </div>
          {eventosConPendientes.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {eventosConPendientes.map((evento) => {
                const pendientes = evento.pendientes || [];
                // U-101: días restantes y fecha con el día local del evento
                const fechaEvento = parsearFechaServidor(String(evento.fecha_evento || '').slice(0, 10)) || new Date(NaN);
                const diasRestantes = diasHastaFecha(evento.fecha_evento);
                const nombreEmpresa = evento.cliente_nombre || evento.cliente_data?.nombre_empresa || evento.cliente_data?.nombre || 'Sin cliente';
                // Determinar si el evento está completo (sin pendientes)
                const eventoCompleto = pendientes.length === 0;
                const textoDias = diasRestantes === 0 ? 'HOY' : diasRestantes === 1 ? 'Mañana' : `${diasRestantes} d`;
                let lineaFecha = fechaEvento.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
                if (evento.hora_inicio) lineaFecha += ` · ${evento.hora_inicio}`;
                if (evento.etiqueta_jornada) lineaFecha += ` · ${evento.etiqueta_jornada}`;
                const tituloFecha = [
                  lineaFecha,
                  evento.num_personas_jornada ? `${evento.num_personas_jornada} personas` : '',
                  evento.lugar_evento || '',
                ].filter(Boolean).join(' · ');

                return (
                  <div
                    key={evento.id}
                    data-testid="evento-proximo"
                    onClick={() => navigate(`/presupuestos/${evento.id}`)}
                    className={`rounded-lg border p-2.5 min-w-0 cursor-pointer transition-colors ${
                      eventoCompleto
                        ? 'bg-green-50 border-green-200 hover:bg-green-100'
                        : 'bg-red-50 border-red-100 hover:bg-red-100'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-sm font-bold text-gray-900">#{evento.numero}</span>
                      <span className={`text-[11px] font-semibold rounded-full px-1.5 py-0.5 ${
                        diasRestantes <= 3 ? 'bg-red-200 text-red-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {textoDias}
                      </span>
                    </div>
                    <p className="text-xs text-gray-800 truncate" title={nombreEmpresa}>{nombreEmpresa}</p>
                    <p className="text-[11px] text-gray-500 truncate" title={tituloFecha}>{lineaFecha}</p>
                    <div className="mt-1.5">
                      {eventoCompleto ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-200 text-green-800 text-[10px] font-bold rounded-full">
                          <CheckCircle2 className="w-3 h-3" /> Listo
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {pendientes.map((item) => (
                            <span
                              key={item.clave}
                              className="text-[10px] font-medium rounded px-1 py-0.5 bg-red-100 text-red-800 cursor-help"
                              title={item.detalle}
                            >
                              {item.texto}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-500" />
            <p className="text-sm font-medium">¡Todo en orden!</p>
            <p className="text-xs text-gray-400">No hay eventos con pendientes en los próximos {diasPendientes} días</p>
          </div>
        )}
      </div>

      {/* Gráfica de Ventas */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Ventas Anuales</h2>
            <p className="text-sm text-gray-500 mt-1">Presupuestos confirmados - Base imponible (sin IVA)</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Selector de tipo de gráfico */}
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-lg">
              <button
                onClick={() => setTipoGrafico('bar')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  tipoGrafico === 'bar'
                    ? 'bg-duetto-primary text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Gráfico de barras"
              >
                📊 Barras
              </button>
              <button
                onClick={() => setTipoGrafico('line')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  tipoGrafico === 'line'
                    ? 'bg-duetto-primary text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Gráfico de líneas"
              >
                📈 Líneas
              </button>
            </div>
            
            <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 mr-2">Comparar años:</span>
            {ventasData && Object.keys(ventasData).length > 0 ? (
              Object.keys(ventasData).sort((a, b) => b - a).slice(0, 5).sort((a, b) => a - b).map(año => (
                <button
                  key={año}
                  onClick={() => toggleAñoComparacion(parseInt(año))}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    añosComparar.includes(parseInt(año))
                      ? 'bg-duetto-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {año}
                </button>
              ))
            ) : (
              // Generar años dinámicamente: año actual, anterior y siguiente
              (() => {
                const currentYear = new Date().getFullYear();
                return [currentYear - 1, currentYear, currentYear + 1].map(año => (
                  <button
                    key={año}
                    onClick={() => toggleAñoComparacion(año)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      añosComparar.includes(año)
                        ? 'bg-duetto-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {año}
                  </button>
                ));
              })()
            )}
          </div>
          </div>
        </div>

        {loadingVentas ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Cargando datos de ventas...</div>
          </div>
        ) : chartData ? (
          <div className="h-96">
            {tipoGrafico === 'bar' ? (
              <Bar data={chartData} options={chartOptions} plugins={[objetivoMarkerPlugin]} />
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">No hay datos disponibles</div>
          </div>
        )}

        {/* Resumen de totales - ordenados de más reciente a más antiguo */}
        {ventasData && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-400 mb-3">
              Importes sin IVA · «Acumulado a hoy» = facturación hasta el {new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} de cada año (comparación a misma fecha) · Ticket medio = total del año / nº de eventos.{proyeccion.activo ? ` · La franja clara del año en curso es la previsión (lo que falta por cerrar para el objetivo). Objetivo de cada mes = media histórica de ese mismo mes${proyeccion.factorAplicado ? `, ajustada por el ritmo del año (${proyeccion.factor >= 1 ? '+' : ''}${Math.round((proyeccion.factor - 1) * 100)}% vs histórico, con ${proyeccion.mesesCerrados} meses cerrados)` : ' (aún sin ajuste por ritmo: faltan meses cerrados)'}. La marca roja discontinua en los meses ya cerrados de ${añoActual} es el objetivo del mes (si la barra la supera, se cumplió). Excluidos 2020-2021 y ene-abr 2022 (COVID).` : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...añosComparar].sort((a, b) => b - a).map((año) => {
                const total = ventasData[año]?.reduce((sum, val) => sum + val, 0) || 0;
                const r = resumenVentas[año] || { total: 0, count: 0, acumuladoHoy: 0 };
                const ticketMedio = r.count > 0 ? r.total / r.count : 0;
                // Color fijo por año, consistente con el gráfico
                const color = colorDeAño(año);
                const fmt = (n) => `€${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (
                  <div key={año} className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-semibold text-gray-700" style={{ color }}>{año}</p>
                    <div className="mt-2 space-y-2">
                      <div>
                        <p className="text-xs text-gray-500">Total año</p>
                        <p className="text-2xl font-bold" style={{ color }}>{fmt(total)}</p>
                      </div>
                      <div className="flex justify-between gap-3 pt-2 border-t border-gray-200">
                        <div>
                          <p className="text-xs text-gray-500">Acumulado a hoy</p>
                          <p className="text-base font-bold text-gray-800">{fmt(r.acumuladoHoy)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Ticket medio</p>
                          <p className="text-base font-bold text-gray-800">{fmt(ticketMedio)}</p>
                          <p className="text-[10px] text-gray-400">{r.count} eventos</p>
                        </div>
                      </div>
                      {año === añoActual && proyeccion.activo && (() => {
                        const totalAnterior = (ventasData[añoActual - 1] || []).reduce((s, v) => s + v, 0);
                        const vsAnterior = totalAnterior > 0 ? Math.round((proyeccion.totalEstimado / totalAnterior - 1) * 100) : null;
                        return (
                          <div className="pt-2 border-t border-gray-200 space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500">Facturado (meses cerrados)</span>
                              <span className="font-semibold text-gray-700">{fmt(proyeccion.facturadoCerrado)}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500">Previsión resto del año</span>
                              <span className="font-semibold text-gray-700">{fmt(proyeccion.previsionResto)}</span>
                            </div>
                            <div className="flex justify-between items-baseline pt-1 border-t border-dashed border-gray-200">
                              <span className="text-xs font-semibold text-gray-600">Proyección cierre</span>
                              <span className="text-lg font-bold" style={{ color }}>{fmt(proyeccion.totalEstimado)}</span>
                            </div>
                            <p className="text-[10px] text-gray-400">
                              {proyeccion.factorAplicado ? `ritmo ${proyeccion.factor >= 1 ? '+' : ''}${Math.round((proyeccion.factor - 1) * 100)}% vs histórico` : 'media histórica (sin ajuste aún)'}{vsAnterior != null ? ` · ${vsAnterior >= 0 ? '+' : ''}${vsAnterior}% vs ${añoActual - 1}` : ''}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Cancelaciones del año: total, importe perdido, antelación y motivos (formato nuevo) */}
      {formatoNuevo && <CancelacionesCard />}

      <OrdenCocinaDescargaModal open={showOrdenesCocina} onClose={() => setShowOrdenesCocina(false)} presupuesto={null} />
    </div>
  );
};

export default Dashboard;